import React, {
  forwardRef,
  PropsWithoutRef,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Container,
  Dropdown,
  Option,
  Options,
  StyledChevron,
} from "./Select.styled";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { InputWithValidation } from "../Input/InputWithValidation";
import { IconTriangle } from "../../assets/icons/IconTriangle";

export interface SelectProps {
  name?: string;
  id?: string;
  value?: string;
  label?: string;
  options?: { value: string; label: string }[];
  onChange?: (value?: string | string[]) => void;
  onBlur: (id: string, value: string) => void;
  required?: boolean;
  error?: string;
  isDisabled?: boolean;
  schema?: any;
}

export const Select = forwardRef<
  HTMLInputElement,
  PropsWithoutRef<SelectProps>
>((props, ref) => {
  const {
    onBlur,
    onChange,
    id,
    value,
    options,
    label,
    name,
    required = false,
    error,
    isDisabled,
    schema,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const updateInputValue = (val: string) => {
    setInputValue(val);
    if (val) {
      showDropdown();
    } else {
      hideDropdown();
    }
  };

  const hideDropdown = () => {
    setIsDropdownVisible(false);
    setInputValue("");
  };

  const showDropdown = () => {
    setIsDropdownVisible(true);
  };

  const outsideAction = () => {
    hideDropdown();
    setInputValue("");
    if (!value && isDropdownVisible) {
      onBlur(id || "", value || "");
    }
  };

  useOutsideClick(containerRef, outsideAction);

  const valueOption = useMemo(
    () => options?.find((option) => option.value === value),
    [options, value]
  );

  const filteredOptions = useMemo(
    () =>
      options?.filter(({ label }: any) =>
        label
          .replaceAll(" ", "")
          .toLowerCase()
          .includes(inputValue.toLowerCase().replaceAll(" ", ""))
      ),
    [options, inputValue]
  );

  const onKeyDown = (keyEvent: React.KeyboardEvent<HTMLInputElement>) => {
    const { key, altKey } = keyEvent;

    if (key === "Enter") {
      keyEvent.preventDefault();
      isDropdownVisible ? hideDropdown() : showDropdown();
    }
    if (key === "Space" || (key === "ArrowDown" && altKey)) {
      showDropdown();
    }
    if (key === "Escape" || (key === "ArrowUp" && altKey)) {
      hideDropdown();
    }
  };

  return (
    <Container ref={containerRef} isDisabled={Boolean(isDisabled)}>
      <InputWithValidation
        id={id}
        value={inputValue}
        onChange={(e) => updateInputValue(e)}
        placeholder={valueOption?.label || ""}
        label={label}
        error={error}
        name={name ?? ""}
        onClick={showDropdown}
        onKeyDown={(keyEvent: React.KeyboardEvent<HTMLInputElement>) => {
          onKeyDown(keyEvent);
        }}
        autoComplete="off"
        iconEnd={
          <StyledChevron
            rotated={isDropdownVisible}
            onClick={isDropdownVisible ? hideDropdown : showDropdown}
          >
            <IconTriangle />
          </StyledChevron>
        }
      />

      {!!filteredOptions?.length && (
        <Dropdown isDropdownVisible={isDropdownVisible}>
          <Options ref={dropdownRef}>
            {filteredOptions?.map((option: any, idx: any) => {
              return (
                <Option
                  type="button"
                  key={option.value}
                  onClick={() => {
                    onChange?.(option.value);
                    hideDropdown();
                    setInputValue("");
                    const selectId = id?.replace("root_", "");
                    const nextElementId =
                      Object.keys(schema?.properties)?.findIndex(
                        (id) => id === selectId
                      ) + 1;

                    const nextElementName = Object.keys(schema?.properties)[
                      nextElementId
                    ];

                    setTimeout(() => {
                      nextElementName
                        ? document
                            ?.getElementById(`root_${nextElementName}`)
                            ?.focus()
                        : //@ts-ignore
                          document?.querySelector(".submit_button")?.focus();
                    }, 0);
                  }}
                  isSelected={option.value === value}
                >
                  {option.label}
                </Option>
              );
            })}
          </Options>
        </Dropdown>
      )}
    </Container>
  );
});
